import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ClipboardCheck, ArrowRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const REQUIRED_STEPS = ['about_you', 'program', 'teams', 'goals'];

/** Spec Section 8: existing users are never forced back into onboarding —
 * just a dismissible reminder, unlike the hard redirect a brand-new signup gets.
 * Visibility is based on the raw steps (hide once 1-4 are done), NOT on the
 * lifecycle `state` string — an already-ACTIVATED backfilled customer with
 * empty steps still needs this card; a genuinely-new mid-wizard user with
 * IN_PROGRESS steps should not see it once steps 1-4 are filled in either. */
export default function OnboardingBanner() {
  const [progress, setProgress] = useState(null);
  const [dismissed, setDismissed] = useState(true);
  const [checked, setChecked] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/onboarding/progress`, authHeaders()).then((res) => {
      const steps = res.data.steps || {};
      const requiredDone = REQUIRED_STEPS.every((s) => steps[s]);
      if (!requiredDone) setProgress(res.data);
      setDismissed(!!res.data.banner_dismissed_at);
      setChecked(true);
    }).catch(() => setChecked(true));
  }, []);

  if (!checked || !progress || dismissed) return null;

  // "Established" = the account already has real product usage (first value
  // achieved, required steps previously marked complete, or fully activated)
  // even though `steps` itself is empty/partial (e.g. backfilled pre-Phase-3
  // customers) — a softer, profile-flavored ask instead of "finish setting up".
  const established = ['REQUIRED_COMPLETE', 'FIRST_VALUE', 'ACTIVATED'].includes(progress.state);
  const title = established ? 'Tell us about your program so Coach AI can personalize' : 'Finish setting up PAC';
  const subtitle = established
    ? 'Takes about 2 minutes — helps Coach AI ground its answers in your program.'
    : `You're ${progress.percent_complete}% done — it only takes a couple more minutes.`;

  const handleDismiss = async () => {
    setDismissed(true);
    try { await axios.post(`${API}/onboarding/dismiss-banner`, {}, authHeaders()); } catch (e) { /* non-fatal */ }
  };

  return (
    <div className="rounded-xl border-2 border-accent bg-accent/10 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="onboarding-banner">
      <ClipboardCheck className="h-7 w-7 text-accent-foreground flex-shrink-0" />
      <div className="flex-1">
        <p className="font-semibold" data-testid="onboarding-banner-title">{title}</p>
        <p className="text-sm text-muted-foreground" data-testid="onboarding-banner-subtitle">{subtitle}</p>
      </div>
      <Button onClick={() => navigate('/onboarding')} data-testid="onboarding-banner-resume-btn">
        Resume setup <ArrowRight className="h-4 w-4 ml-2" />
      </Button>
      <button onClick={handleDismiss} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="onboarding-banner-dismiss-btn">
        <X className="h-5 w-5" />
      </button>
    </div>
  );
}
